import React, { useState } from "react";
import { DatePicker, Popover } from "antd";
import dayjs from "dayjs";
import { useTimeFilter, PRESETS } from "../contexts/TimeFilterContext";

const { RangePicker } = DatePicker;

const PRESET_KEYS = ["last24h", "last7d", "lastCampaign", "custom"];
const ALL_LABELS = { ...PRESETS, custom: { label: "Custom" } };

export default function TimeFilterControl() {
  const { mode, preset, dateRange, setPreset, setCustomRange } = useTimeFilter();
  const [open, setOpen] = useState(false);

  const isLive = mode === "live";

  const handlePreset = (key) => {
    setPreset(key);
    if (key !== "custom") setOpen(false);
  };

  const buttonLabel = isLive
    ? "Live"
    : preset === "custom" && dateRange
    ? `${dateRange[0].format("MMM D")} – ${dateRange[1].format("MMM D, YYYY")}`
    : ALL_LABELS[preset]?.label ?? "Historical";

  const content = (
    <div className="w-64 space-y-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
        Time range
      </p>
      <div className="grid grid-cols-2 gap-1.5">
        {PRESET_KEYS.map((key) => (
          <button
            key={key}
            onClick={() => handlePreset(key)}
            className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
              preset === key
                ? "bg-brand-500 text-white"
                : "border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 dark:border-white/10 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-white/5"
            }`}
          >
            {ALL_LABELS[key]?.label ?? key}
          </button>
        ))}
      </div>
      {preset === "custom" && (
        <RangePicker
          size="small"
          className="w-full"
          value={dateRange}
          disabledDate={(current) => current && current.isAfter(dayjs(), "day")}
          onChange={(dates) => {
            if (dates?.[0] && dates?.[1]) {
              setCustomRange(dates[0], dates[1]);
              setOpen(false);
            }
          }}
        />
      )}
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="bottomRight"
    >
      <button
        aria-label={`Time filter: ${buttonLabel}. Click to change.`}
        aria-expanded={open}
        className={`flex h-[30px] items-center gap-1.5 rounded-full border px-3 text-xs font-medium transition-colors ${
          isLive
            ? "border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-white/10 dark:bg-navy-800 dark:text-gray-300 dark:hover:bg-white/5"
            : "border-brand-400 bg-brand-50 text-brand-600 hover:bg-brand-100 dark:border-brand-500/50 dark:bg-brand-900/30 dark:text-brand-400"
        }`}
      >
        <span aria-hidden="true" className="text-sm">
          {isLive ? "⏱" : "📅"}
        </span>
        <span className="whitespace-nowrap">{buttonLabel}</span>
      </button>
    </Popover>
  );
}
